import { sumArrayLike } from "$root/utils";
import { Column, Row, Text } from "@mdotm/shadcn/legacy/components";
import { ForEach } from "@mdotm/utils/react-extra";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { useMemo } from "react";
import { BulkActionNotSuitableSummary } from "./not-suitable-summary";
import type {
	BulkActionAvailabilityResult,
	ReasonToMessageMap,
	ReviewDialogMidSection,
	ReviewSectionLabels,
} from "./types";

const colorByPalette: Record<"success" | "alert" | "info", string> = {
	success: themeCSSVars.palette_P200,
	alert: themeCSSVars.palette_A200,
	info: themeCSSVars.palette_S200,
};

export function BulkActionReviewSections<Entity extends { name?: string }, Reason extends string>({
	availabilityResult,
	reasonToMessage,
	midSections,
	getRedirectUrl,
	hideRedirectUrl,
	showEntityNames,
	labels,
}: {
	availabilityResult: BulkActionAvailabilityResult<Entity, Reason>;
	reasonToMessage: ReasonToMessageMap<Reason>;
	midSections?: ReviewDialogMidSection[];
	getRedirectUrl(entityUuids: string[]): string;
	hideRedirectUrl?: boolean;
	showEntityNames?: boolean;
	labels: ReviewSectionLabels;
}): React.JSX.Element {
	const [available, unavailable] = availabilityResult;
	const unavailableCount = useMemo(
		() => (!unavailable ? 0 : sumArrayLike(Object.values(unavailable) as Entity[][], (x) => x.length)),
		[unavailable],
	);

	const redirectUrl = useMemo(
		() =>
			getRedirectUrl(
				unavailable
					? Object.values(unavailable).flatMap((items) => (items as Array<{ uuid?: string }>).map((x) => x.uuid!))
					: [],
			),
		[unavailable, getRedirectUrl],
	);

	const { entitySingular, entityPlural } = labels;

	const sections = useMemo(
		() => [
			...(available.length === 0 && ((midSections?.length ?? 0) > 0 || unavailableCount > 0)
				? []
				: [
						{
							count: available.length,
							title:
								available.length === 1
									? `${entitySingular} is ready to be processed`
									: `${entityPlural} are ready to be processed`,
							palette: "success" as const,
						},
					]),
			...(midSections ?? []),
			...(unavailable && unavailableCount > 0
				? [
						{
							count: unavailableCount,
							title:
								unavailableCount === 1
									? `${entitySingular} will not be included`
									: `${entityPlural} will not be included`,
							children: (
								<BulkActionNotSuitableSummary
									unavailable={unavailable}
									reasonToMessage={reasonToMessage}
									redirectUrl={redirectUrl}
									hideRedirectUrl={hideRedirectUrl}
									showEntityNames={showEntityNames}
								/>
							),
							palette: "alert" as const,
						},
					]
				: []),
		],
		[
			available.length,
			midSections,
			unavailable,
			unavailableCount,
			redirectUrl,
			entitySingular,
			entityPlural,
			reasonToMessage,
			hideRedirectUrl,
			showEntityNames,
		],
	);

	return (
		<ForEach collection={sections}>
			{(section, index) => (
				<Column flexGrow={0}>
					<Row gap={16} flexGrow={1} alignItems="start">
						<Text
							data-qualifier="EligibilityWarning/AvailableEntities"
							as="div"
							className="min-w-[32px] py-1 px-2 text-center rounded-lg"
							type="Body/L/Bold"
							style={{
								backgroundColor: colorByPalette[section.palette ?? "info"],
							}}
						>
							{section.count}
						</Text>
						<Column flexGrow={1}>
							<Text as="div" type="Body/L/Medium" className="mt-1">
								{section.title}
							</Text>
							{section.children && (
								<Column flexGrow={0} className="my-4">
									{section.children}
								</Column>
							)}
						</Column>
					</Row>
					{index < sections.length - 1 && (
						<div className="border-b w-10 my-4" style={{ borderBottomColor: themeCSSVars.palette_N100 }} />
					)}
				</Column>
			)}
		</ForEach>
	);
}
